import type { ErrorEnvelope } from "@chat-yai/shared";
import { authStorage } from "@/lib/auth-storage";

export class ApiError extends Error {
  public readonly status: number;
  public readonly code: string;
  public readonly requestId: string;
  public readonly details: Record<string, unknown> | undefined;

  constructor(
    status: number,
    code: string,
    message: string,
    requestId: string,
    details?: Record<string, unknown>,
  ) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.requestId = requestId;
    this.details = details;
  }
}

export interface RequestOptions {
  method?: "GET" | "POST" | "PATCH" | "DELETE" | "PUT";
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined | null>;
  signal?: AbortSignal;
  skipAuth?: boolean;
  headers?: Record<string, string>;
}

const API_BASE_URL = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? "/api";

function buildUrl(path: string, query?: RequestOptions["query"]): string {
  const url = new URL(
    path.startsWith("http") ? path : API_BASE_URL.replace(/\/$/, "") + path,
    typeof window !== "undefined" ? window.location.origin : "http://localhost",
  );
  if (query) {
    for (const [k, v] of Object.entries(query)) {
      if (v === undefined || v === null) continue;
      url.searchParams.set(k, String(v));
    }
  }
  return url.toString();
}

let refreshInflight: Promise<string | null> | null = null;

async function tryRefresh(): Promise<string | null> {
  if (refreshInflight) return refreshInflight;
  refreshInflight = (async () => {
    try {
      const res = await fetch(buildUrl("/auth/refresh"), {
        method: "POST",
        credentials: "include",
        headers: { "content-type": "application/json" },
      });
      if (!res.ok) return null;
      const data = (await res.json()) as { access_token: string };
      authStorage.set(data.access_token);
      return data.access_token;
    } catch {
      return null;
    } finally {
      refreshInflight = null;
    }
  })();
  return refreshInflight;
}

export async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const { method = "GET", body, query, signal, skipAuth, headers = {} } = opts;
  const url = buildUrl(path, query);

  const doFetch = async (token: string | null): Promise<Response> => {
    const init: RequestInit = {
      method,
      credentials: "include",
      signal,
      headers: {
        "content-type": "application/json",
        ...(token && !skipAuth ? { authorization: `Bearer ${token}` } : {}),
        ...headers,
      },
    };
    if (body !== undefined) init.body = JSON.stringify(body);
    return fetch(url, init);
  };

  let token = authStorage.get();
  let res: Response;
  try {
    res = await doFetch(token);
  } catch {
    throw new ApiError(0, "COMMON_NETWORK", "Network error", "");
  }

  if (res.status === 401 && !skipAuth) {
    const refreshed = await tryRefresh();
    if (refreshed) {
      token = refreshed;
      try {
        res = await doFetch(token);
      } catch {
        throw new ApiError(0, "COMMON_NETWORK", "Network error", "");
      }
    }
  }

  if (res.status === 204) return undefined as T;

  const text = await res.text();
  let parsed: unknown = null;
  if (text) {
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = null;
    }
  }

  if (!res.ok) {
    const env = parsed as ErrorEnvelope | null;
    if (env && env.error) {
      throw new ApiError(
        res.status,
        env.error.code,
        env.error.message,
        env.error.request_id,
        env.error.details,
      );
    }
    throw new ApiError(res.status, "COMMON_UNKNOWN", res.statusText || "Error", "");
  }

  return parsed as T;
}
